import { ISupportGitHubIssueLink } from '../../../models/Support/ISupportGitHub';

/**
 * Presentation for the GitHub panel's lifecycle-first layout: which stage an investigation has
 * reached, how its result reads, and how records group into sections. Pure derivations of the
 * stored panel state — nothing here fetches or infers beyond what the API returns.
 */

export type GitHubPanelTone = 'neutral' | 'progress' | 'positive' | 'caution' | 'critical' | 'engineering';

/**
 * The stages an investigation moves through, in order. Creating the issue is what triggers the
 * workflow, so those are one stage rather than two.
 */
export const INVESTIGATION_STAGES = [
	'Issue created',
	'Investigating',
	'Result',
	'Escalation requested',
	'Engineering issue',
] as const;

export interface InvestigationLifecycle {
	/** 1-based position within INVESTIGATION_STAGES. */
	stage: number;
	total: number;
	stageLabel: string;
	/** True while the stage is waiting on the repository workflow rather than on the agent. */
	inFlight: boolean;
}

export interface GitHubStatusPill {
	label: string;
	tone: GitHubPanelTone;
}

export interface ConfidenceMeter {
	label: string;
	/** Share of the meter to fill; the scale has three steps because the workflow reports three values. */
	fillPercent: number;
	tone: GitHubPanelTone;
}

export interface GitHubPanelSections {
	investigations: ISupportGitHubIssueLink[];
	engineering: ISupportGitHubIssueLink[];
	other: ISupportGitHubIssueLink[];
}

const CONFIDENCE_STEPS: Record<string, { fillPercent: number; tone: GitHubPanelTone }> = {
	high: { fillPercent: 100, tone: 'positive' },
	medium: { fillPercent: 66, tone: 'caution' },
	low: { fillPercent: 33, tone: 'critical' },
};

const hasRequestedEscalation = (issue: ISupportGitHubIssueLink) => !!issue.escalationRequestedTriageRunId;

/**
 * A resolved investigation stops at Result and never advances — the unfilled stages read as
 * "escalation was not needed", which is the outcome worth showing.
 */
export const getInvestigationLifecycle = (issue: ISupportGitHubIssueLink): InvestigationLifecycle => {
	const stage = resolveStage(issue);
	return {
		stage,
		total: INVESTIGATION_STAGES.length,
		stageLabel: INVESTIGATION_STAGES[stage - 1],
		inFlight:
			issue.status === 'Investigating' || (issue.status === 'EscalationRecommended' && hasRequestedEscalation(issue)),
	};
};

const resolveStage = (issue: ISupportGitHubIssueLink): number => {
	switch (issue.status) {
		case 'Investigating':
			return 2;
		case 'Resolved':
			return 3;
		case 'EscalationRecommended':
			return hasRequestedEscalation(issue) ? 4 : 3;
		case 'Escalated':
			return 5;
		default:
			return 1;
	}
};

export const getStatusPill = (issue: ISupportGitHubIssueLink): GitHubStatusPill => {
	switch (issue.status) {
		case 'Investigating':
			return { label: 'Investigating', tone: 'progress' };
		case 'Resolved':
			return { label: 'Resolved', tone: 'positive' };
		case 'EscalationRecommended':
			return hasRequestedEscalation(issue)
				? { label: 'Escalation requested', tone: 'engineering' }
				: { label: 'Escalation recommended', tone: 'caution' };
		case 'Escalated':
			return { label: 'Engineering issue created', tone: 'engineering' };
		default:
			return { label: 'Issue created', tone: 'neutral' };
	}
};

/**
 * Undefined when the stored value is outside the reported scale, so the caller shows the raw text
 * instead of a meter that would imply a level we did not receive.
 */
export const getConfidenceMeter = (confidence: string | undefined): ConfidenceMeter | undefined => {
	if (!confidence) return undefined;
	const normalized = formatConfidence(confidence);
	const step = CONFIDENCE_STEPS[normalized];
	if (!step) return undefined;
	return { label: `${normalized} confidence`, ...step };
};

/** The one spelling of a confidence value the panel shows, wherever it appears. */
export const formatConfidence = (confidence: string): string => confidence.trim().toLowerCase();

/**
 * Escalation stays available until it has been requested. The API tolerates a repeat request, but
 * offering one while the repository workflow is still validating reads as though nothing happened.
 */
export const canEscalateIssue = (issue: ISupportGitHubIssueLink): boolean =>
	issue.kind === 'Triage' &&
	issue.status === 'EscalationRecommended' &&
	!!issue.latestTriageRunId &&
	!hasRequestedEscalation(issue);

export const hasInvestigationResult = (issue: ISupportGitHubIssueLink): boolean =>
	!!issue.latestTriageRunId && !!issue.latestResultAt;

export const groupIssuesByKind = (issues: ISupportGitHubIssueLink[]): GitHubPanelSections => ({
	investigations: issues.filter(issue => issue.kind === 'Triage'),
	engineering: issues.filter(issue => issue.kind === 'Engineering'),
	other: issues.filter(issue => issue.kind !== 'Triage' && issue.kind !== 'Engineering'),
});

/** The triage issue an engineering issue was filed from, so the card can cite its origin. */
export const findSourceIssue = (
	issue: ISupportGitHubIssueLink,
	issues: ISupportGitHubIssueLink[]
): ISupportGitHubIssueLink | undefined =>
	issue.sourceIssueLinkId ? issues.find(candidate => candidate.id === issue.sourceIssueLinkId) : undefined;
